<!-- eslint-disable no-undef -->
<script setup>
    // Imports
    import { ref, onMounted } from 'vue';
    import { useStore } from 'vuex';
    import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
    import Users from '@/api/users';
    import InternationalPhoneInput from '@/components/common/InternationalPhoneInput.vue';
    import { useModalBackdropClose } from '@/composables/useModalBackdropClose';


    // Definitions
    const props = defineProps({
        providedNumber: {
            type: String,
            required: false,
            default: ''
        },
    });

    const store = useStore();

    const emit = defineEmits(['close', 'verified']);

    const loading = ref(false);
    const error = ref(null);
    const verified = ref(false);
    const agreement = ref(false);
    const codeSent = ref(false);
    const code = ref(['', '', '', '']);
    const codeInputs = ref([]);
    const timer = ref(0);
    const phoneNumber = ref(props.providedNumber);
    const requestID = ref('');
    
    // Lifecycle hooks
    onMounted(async() => {
        return;
    });

    // Methods
    const sendVerificationCode = async() => {

        // Send verification code
        loading.value = true;
        const response = await Users.post('/request-verification', {
            phoneNumber: phoneNumber.value, // Should already be clean numerical format
        });
        requestID.value = response.details.request_id;

        // Start timer
        timer.value = 30;
        const interval = setInterval(() => {
            timer.value--;
        }, 1000);
        setTimeout(() => {
            clearInterval(interval);
            error.value = '';
        }, 30000);

        // Set codeSent to true
        codeSent.value = true;

        // Set loading to false
        loading.value = false;
    }

    const handlePaste = (index, event) => {
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text');
        const digits = pastedData.replace(/\D/g, ''); // Remove non-digits
        const digitsArray = digits.slice(0, 5).split(''); // Take first 5 digits
        
        // Fill the code array starting from current index
        for (let i = 0; i < digitsArray.length && (index + i) < 5; i++) {
            code.value[index + i] = digitsArray[i];
        }
        
        // Focus on the last filled input or the last input
        const lastIndex = Math.min(index + digitsArray.length - 1, 4);
        setTimeout(() => {
            if (codeInputs.value[lastIndex]) {
                codeInputs.value[lastIndex].focus();
            }
        }, 0);
    };

    const handleDigitInput = (index, event) => {
        const value = event.target.value;
        
        // Only allow numbers
        if (!/^\d*$/.test(value)) {
            code.value[index] = '';
            return;
        }
        
        // Auto-advance to next input
        if (value && index < 4) {
            setTimeout(() => {
                const nextInput = codeInputs.value[index + 1];
                if (nextInput) {
                    nextInput.focus();
                }
            }, 0);
        }
    };
    
    const handleKeyDown = (index, event) => {
        // Handle backspace
        if (event.key === 'Backspace' && !code.value[index] && index > 0) {
            const prevInput = codeInputs.value[index - 1];
            if (prevInput) {
                prevInput.focus();
            }
        }
    };

    const verifyNumber = async() => {
        loading.value = true;
        const codeString = code.value.join('');
        const response = await Users.post('/verify-code', {
            phoneNumber: phoneNumber.value,
            requestID: requestID.value,
            code: codeString,
        });
        if (response.status === 'success') {
            verified.value = true;
            store.dispatch('setUser', {
                ...store.state.user,
                phone: phoneNumber.value,
            });
            setTimeout(() => {
                emit('close');
            }, 1000);
            emit('verified');
        } else {
            error.value = 'Invalid code, try again';
        }
        loading.value = false;
    }

    const cancelVerification = async() => {
        await Users.post('/cancel-verification', {
            requestID: requestID.value,
        });
        emit('close');
    }

    const close = () => {
        emit('close');
    };

    const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal p-[20px] rounded-lg w-[600px] max-h-[calc(100vh-50px)] overflow-y-auto" @mousedown="onContentMousedown">
      <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
      <h2 class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Verify Phone Number</h2>
      <InternationalPhoneInput v-if="!providedNumber" v-model="phoneNumber" class="mb-4" />
        <div v-if="!codeSent" class="flex flex-col">
            <div class="flex flex-row gap-2 mb-4">
                <input type="checkbox" id="agreement" name="agreement" class="w-4 h-4 mt-[6px]" v-model="agreement">
                <label for="agreement" class="text-lg text-mc-ink dark:text-on-dark">I have read and agree to the
                    <a href="https://meshcommengineering.com/privacy-policy/" target="_blank" class="text-blue-500">privacy policy</a> 
                    and 
                    <a href="https://meshcommengineering.com/terms-and-conditions/" target="_blank" class="text-blue-500">terms and conditions.</a>
                </label>
            </div>
            <p class="text-mc-ink dark:text-on-dark mb-4">By saving your phone number to your account, you agree
                to recieve text messages from Meshcomm Engineering LLC at the number provided. Message frequency varies. 
                Message and data rates may apply. You can opt out at any time by editing your account settings or replying 
                "STOP" to the message. Text HELP to get help.
            </p>
            <div class="w-full flex justify-center">
                <LoadingAnimation3 v-if="loading" size="20px"/>
                <button v-else class="mt-4 w-full" :class="agreement && phoneNumber ? 'default-button' : 'default-button-off'" :disabled="!termsAndConditions || !agreement || !phoneNumber" @click="sendVerificationCode">Send Code</button>
            </div>
        </div>
        <div v-else class="flex flex-col items-center">
            <p class="text-lg text-mc-ink dark:text-on-dark mb-6">A verification code has been sent to your phone number.</p>
            <div class="flex flex-col items-center">
                <label class="text-lg text-mc-ink dark:text-on-dark mb-4">Enter the 5 digit code:</label>
                <p v-if="error" class="text-red-500 mb-4">{{ error }}</p>
                <div class="flex gap-3 justify-center" :class="verified ? 'border-2 border-green-500 p-2 rounded-lg' : ''">
                    <input 
                        v-for="(digit, index) in 4" 
                        :key="index"
                        :ref="el => codeInputs[index] = el"
                        type="text" 
                        maxlength="1"
                        v-model="code[index]"
                        @input="handleDigitInput(index, $event)"
                        @paste="handlePaste(index, $event)"
                        @keydown="handleKeyDown(index, $event)"
                        class="w-12 h-12 text-center text-2xl font-bold border-b-2 border-mc-steel dark:border-line-on-dark focus:border-mc-red focus:outline-none bg-transparent text-mc-ink dark:text-on-dark"
                        inputmode="numeric"
                    />
                </div>
            </div>
            <p v-if="timer > 0" class="text-lg text-ink-muted dark:text-on-dark-muted">Code expires in {{ timer }} seconds</p>
            <div class="flex flex-row justify-end gap-2 mt-6">
                <button class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160" @click="cancelVerification">Cancel</button>
                <button v-if="timer > 0 && !loading" class="default-button" @click="verifyNumber">Verify</button>
                <button v-if="timer === 0 && !loading" class="default-button" @click="sendVerificationCode">Resend Code</button>
                <LoadingAnimation3 v-if="loading && !verified" size="20px"/>
            </div>
        </div>
    </div>
  </div>
</template>
